import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import {
  type CATEGORIES_ENUM,
  type COMPETITION_LEVELS_ENUM,
  ORDER_BY,
  type ORDER_BY_ENUM,
  type Opportunities,
  type QUOTES_GRADES_ENUM,
  SORT_ORDER,
  type SORT_ORDER_ENUM,
} from '~/api/quotes/types'

import translations from './useFetchSourcingOpportunities.translations'

export interface UseFetchSourcingOpportunitiesOptions {
  marketCodes?: MarketCountryCode[]
  model?: string
  brand?: string
  grades?: QUOTES_GRADES_ENUM[]
  categories?: CATEGORIES_ENUM[]
  pageNumber?: number
  pageSize?: string
  sortBy?: ORDER_BY_ENUM
  sortOrder?: SORT_ORDER_ENUM
  competitionLevels?: COMPETITION_LEVELS_ENUM[]
}

export const defaultOptions = {
  marketCodes: [],
  model: '',
  brand: '',
  grades: [],
  categories: [],
  pageNumber: 1,
  pageSize: '10',
  sortBy: ORDER_BY.PROFIT_POTENTIAL,
  sortOrder: SORT_ORDER.DESC,
  competitionLevels: [],
}

const STORAGE_KEY = 'sourcingOpportunities_lastSearch'

export function useFetchSourcingOpportunities(
  options: UseFetchSourcingOpportunitiesOptions = {},
) {
  const $httpFetch = useHttpFetch()
  const { openErrorToast } = useTheToast()
  const i18n = useI18n()
  const localStorage = useLocalStorage()

  // Create reactive refs for all query params with initial options or defaults
  const marketCodes = ref<MarketCountryCode[]>(
    options.marketCodes ?? defaultOptions.marketCodes,
  )
  const model = ref<string>(options.model ?? defaultOptions.model)
  const brand = ref<string>(options.brand ?? defaultOptions.brand)
  const grades = ref<QUOTES_GRADES_ENUM[]>(
    options.grades ?? defaultOptions.grades,
  )
  const categories = ref<CATEGORIES_ENUM[]>(
    options.categories ?? defaultOptions.categories,
  )
  const pageSize = ref<string>(options.pageSize ?? defaultOptions.pageSize)
  const sortBy = ref<ORDER_BY_ENUM>(options.sortBy ?? defaultOptions.sortBy)
  const sortOrder = ref<SORT_ORDER_ENUM | undefined>(
    options.sortOrder ?? undefined,
  )
  const pageNumber = ref<number>(
    options.pageNumber ?? defaultOptions.pageNumber,
  )
  const competitionLevels = ref<COMPETITION_LEVELS_ENUM[]>(
    options.competitionLevels ?? defaultOptions.competitionLevels,
  )

  const opportunities = ref<Opportunities | null>(null)
  const error = ref<Error | null>(null)
  const status = ref<string | null>('pending')

  // Compute the query object from reactive refs
  const query = computed(() =>
    removeEmptyValuesInObject({
      marketCodes: marketCodes.value,
      model: model.value,
      brand: brand.value,
      grades: grades.value,
      categories: categories.value,
      pageSize: pageSize.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
      pageNumber: pageNumber.value,
      competitionLevels: competitionLevels.value,
    }),
  )

  function reset() {
    marketCodes.value = defaultOptions.marketCodes
    categories.value = defaultOptions.categories
    grades.value = defaultOptions.grades
    sortBy.value = defaultOptions.sortBy
    sortOrder.value = undefined
    brand.value = defaultOptions.brand
    model.value = defaultOptions.model
    pageSize.value = defaultOptions.pageSize
    competitionLevels.value = defaultOptions.competitionLevels
  }

  async function fetch() {
    status.value = 'pending'
    try {
      opportunities.value = await $httpFetch(
        '/buyback-quotes/api/v1/sourcing-opportunities',
        {
          method: 'GET',
          query: query.value,
        },
      )
      status.value = 'success'
    } catch (err) {
      error.value = err as Error
      status.value = 'error'
    }
  }

  function getStringifiedQuery() {
    return JSON.stringify({
      marketCodes: marketCodes.value,
      brand: brand.value,
      grades: grades.value,
      categories: categories.value,
      competitionLevels: competitionLevels.value,
    })
  }

  const debouncedFetch = useDebounceFn(fetch, 500)

  watch(
    [
      pageNumber,
      marketCodes,
      model,
      brand,
      grades,
      categories,
      pageSize,
      sortBy,
      sortOrder,
      competitionLevels,
    ],
    ([newPageNumber], [oldPageNumber]) => {
      if (newPageNumber === oldPageNumber) {
        if (pageNumber.value !== 1) {
          pageNumber.value = 1

          return
        }
      }
      void debouncedFetch()
    },
    { deep: true },
  )

  watch(error, (newError) => {
    if (newError) {
      openErrorToast({
        content: i18n(translations.errorGettingOpportunities),
      })
    }
  })

  // Restore last search from localStorage and fetch data on mount
  onMounted(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && stored !== getStringifiedQuery()) {
      const lastSearch = JSON.parse(
        stored,
      ) as UseFetchSourcingOpportunitiesOptions
      if (lastSearch.marketCodes !== undefined) {
        marketCodes.value = lastSearch.marketCodes
      }
      if (lastSearch.brand !== undefined) {
        brand.value = lastSearch.brand
      }
      if (lastSearch.grades !== undefined) {
        grades.value = lastSearch.grades
      }
      if (lastSearch.categories !== undefined) {
        categories.value = lastSearch.categories
      }
      if (lastSearch.competitionLevels !== undefined) {
        competitionLevels.value = lastSearch.competitionLevels
      }
    }

    void fetch()
  })

  onUnmounted(() => {
    localStorage.setItem(STORAGE_KEY, getStringifiedQuery())
  })

  return {
    opportunities,
    error,
    status,

    marketCodes,
    model,
    brand,
    grades,
    categories,
    pageNumber,
    pageSize,
    sortBy,
    sortOrder,
    competitionLevels,

    fetch,
    reset,
  }
}
